import { useLoggedInUser } from '~/pages/_shared';
import { UseIncrementalQueryResult } from '~/queries';
import { UseMutationResult } from '@tanstack/react-query';
import { Selection, useSelection } from '~/ui-components';
import { useGetQuizSessions } from './useGetQuizSessions';
import { useLocalization } from '~/framework';
import { useDebounced } from '@dozuki/web-js/components';
import { useState, useContext, createContext, ReactNode, useEffect } from 'react';
import { GetQuizSessionsOrderByOptions, GetQuizSessionStruct } from '~/monolith-api';
import {
   useBulkCancelQuizSession,
   BulkCancelQuizSessionRequest,
   BulkCancelQuizSessionResponse,
} from '../_shared/useBulkCancelQuizSession';

type QuizSessionsSort = {
   by?: GetQuizSessionsOrderByOptions;
   direction: 'ASC' | 'DESC';
};

export type BaseFilter = {
   label: string;
   value: number | string | boolean | undefined;
   handle: (params: any) => void;
};

export type QuizSessionTeamFilter = {
   name: string;
   id: number;
};

export type QuizSessionsFiltersHandlers = {
   showMyQuizzes: BaseFilter;
   team: BaseFilter;
};

export type QuizSessionsFilters = {
   team?: QuizSessionTeamFilter;
   showMyQuizzes?: boolean;
};

export type QuizSessionsContext = {
   isLoading: boolean;

   quizSessions: GetQuizSessionStruct[];

   queryFormValue: string;
   setQueryFormValue: (value: string) => any;
   query: string;

   hasFilters: boolean;
   filters: QuizSessionsFilters;
   setTeamFilter: (id: number, name: string) => any;
   setFilters: (filters: QuizSessionsFilters) => any;
   filterManagement: QuizSessionsFiltersHandlers;

   sort: QuizSessionsSort;
   setSort: (value: QuizSessionsSort) => any;

   setCancelAllQuizSessions: (value: boolean) => any;

   bulkCancelQuizSession: UseMutationResult<
      BulkCancelQuizSessionResponse,
      unknown,
      BulkCancelQuizSessionRequest,
      unknown
   >;

   selection: Selection<GetQuizSessionStruct>;

   cancelAllQuizSessions: boolean;
   handleCancelAllQuizSession: (value: boolean) => any;

   showCancelModal: boolean;
   setShowCancelModal: (value: boolean) => any;

   showTeamModal: boolean;
   setShowTeamModal: (value: boolean) => any;

   quizSessionsQuery: UseIncrementalQueryResult<GetQuizSessionStruct[]>;
};

export const QuizSessionsContext = createContext<QuizSessionsContext>({} as QuizSessionsContext);

type QuizSessionsProviderProps = {
   children: ReactNode;
};

export function QuizSessionsProvider({ children }: QuizSessionsProviderProps) {
   const { _js } = useLocalization();

   const { data: user } = useLoggedInUser();

   const [filters, setFilters] = useState<QuizSessionsFilters>({});

   const [queryFormValue, setQueryFormValue] = useState('');

   const query = useDebounced(queryFormValue, 500);

   const hasFilters = Boolean(query.length || Object.values(filters).length);

   const setTeamFilter = (id: number, name: string) => {
      setShowTeamModal(false);

      return setFilters(current => {
         if (id === 0) {
            const { team, ...rest } = current;
            return rest;
         }
         return { ...current, team: { id, name } };
      });
   };

   const filterManagement = {
      showMyQuizzes: {
         label: _js('Show my quizzes'),
         value: filters.showMyQuizzes,
         handle: () => {
            if (filters.showMyQuizzes) {
               return setFilters(current => {
                  const { showMyQuizzes, ...rest } = current;
                  return rest;
               });
            }

            return setFilters({ showMyQuizzes: true });
         },
      },
      team: {
         label: _js('Filter by team'),
         value: filters.team?.id,
         handle: () => {
            return setShowTeamModal(!showTeamModal);
         },
      },
   };

   const [cancelAllQuizSessions, setCancelAllQuizSessions] = useState<boolean>(false);

   const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
   const [showTeamModal, setShowTeamModal] = useState<boolean>(false);

   const [sort, setSort] = useState<QuizSessionsSort>({ direction: 'DESC' });

   const { by: orderby, direction } = sort;

   const bulkCancelQuizSession = useBulkCancelQuizSession();

   const quizSessionsQuery = useGetQuizSessions({
      query,
      teamid: filters.team?.id,
      showMyQuizzes:
         filters.showMyQuizzes ||
         (!user?.isUserAnyTeamOwner && !user?.permissions.canIViewAllQuizSessions),
      orderby,
      direction,
   });

   let { flattenedData: quizSessions, isLoading } = quizSessionsQuery;

   quizSessions ??= [];

   const handleCancelAllQuizSession = (value: boolean) => {
      if (!value) {
         selection.toggleAll();
      }

      setCancelAllQuizSessions(!cancelAllQuizSessions);
   };

   const selection = useSelection({
      initiallyDisabled: true,
      options: quizSessions || [],
      equalFunc: (a, b) => a.quiz_sessionid === b.quiz_sessionid,
   });

   useEffect(() => {
      if (cancelAllQuizSessions && selection?.selected.length != quizSessions?.length) {
         selection.toggleAll();
      }
   }, [quizSessions, cancelAllQuizSessions, selection]);

   const quizSessionsContext: QuizSessionsContext = {
      isLoading,

      quizSessions,

      queryFormValue,
      setQueryFormValue,
      query,

      hasFilters,
      filters,
      setFilters,
      setTeamFilter,
      filterManagement,

      sort,
      setSort,
      selection,

      cancelAllQuizSessions,
      setCancelAllQuizSessions,
      handleCancelAllQuizSession,

      showCancelModal,
      setShowCancelModal,

      showTeamModal,
      setShowTeamModal,

      quizSessionsQuery,
      bulkCancelQuizSession,
   };

   return (
      <QuizSessionsContext.Provider value={quizSessionsContext}>
         {children}
      </QuizSessionsContext.Provider>
   );
}

export function useQuizSessionsContext() {
   return useContext(QuizSessionsContext);
}
